import { ReactElement, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { cloneDeep, get, isNumber, keys, lowerFirst } from 'lodash';
import { Field, FormikProps, withFormik } from 'formik';
import { KeyboardBackspaceRounded, WarningRounded } from '@material-ui/icons';
import { Box, CircularProgress } from '@material-ui/core';
import classNames from 'classnames';
import { AnySchema } from 'yup';
import { ThunkAction } from 'redux-thunk';

// Components
import InputBlock from 'components/global/inputBlock/inputBlock';
import Button from 'components/Button/Button';
import { Modal } from 'components/ContinuousImprovement';
import Faq from 'components/Faq/Faq';
import { LocationDetailsRepeater } from 'components/LocationDetailsRepeater';
import { HighlightBox } from 'components/HighlightBox';

// Store + Core
import { FormService } from 'services/FormService';
import { isJASelector } from 'store/user/selectors';
import { TAXONOMY_FAQ_ROOT_ID, ACTIVITY_TYPE_ABBREVIATION_ENUM } from 'core/constants';
import {
  Activity,
  ActivityFormStateValues,
  ActivityType,
  BoardMocDetails,
  CommercialSupportSource,
  IBoardRemsDetails,
  IBoardSlim,
  IDictionary,
  IDisabledContext,
  IDisabledState,
  IInKindSupportSourceOption,
  IMonetarySupportSourceOption,
  ITaxonomyTerm,
  PARSAction,
} from 'core/models';
import { ButtonVariant } from 'core/enums';
import { defaultAddFormProps } from '../constants';
import { closeFaq } from 'store/faq/actions';
import { isFaqOpenSelector } from 'store/faq/selectors';
import {
  amaCreditTermIdSelector,
  ipceCreditTermIdSelector,
  pharmacyCreditTermIdSelector,
} from 'store/taxonomy/selectors';
import { boardSelector, configuredBoardsDictionarySelector, configuredBoardsSelector } from 'store/board/selectors';
import { getAllBoards, getBoardById } from 'store/board/actions';
import { activityTypesSelector, activitySelector } from 'store/activity/selectors';

// Misc
import { getAllowedBoardSelections } from '../utils';

// Types
import { BoardTypes, IBCTBoard } from 'layouts/pages/bct/types';

// Hooks
import { useFilterHook } from '../forms/hooks';

// Styles
import styles from './index.module.scss';

// Context
import { DisabledContext } from 'components/forms/DisabledContext';

interface IEditActivityProps {
  activity: Activity;
  boards: IBoardSlim[];
  dictionaries: IDictionary<IDictionary<ITaxonomyTerm>>;
  editIndex: number;
  handleSubmit: (values: ActivityFormStateValues) => Promise<void>;
  idParam: string;
  types: ActivityType[];
  terms: IDictionary<ITaxonomyTerm>;
  schemas: AnySchema[];
}

const EditActivityFormik = (props: IEditActivityProps & FormikProps<ActivityFormStateValues>): ReactElement => {
  const {
    activity,
    boards,
    editIndex,
    errors,
    handleChange,
    handleSubmit,
    idParam,
    initialValues,
    isSubmitting,
    isValid,
    setFieldValue,
    submitForm,
    terms,
    touched,
    types,
    values,
  } = props;
  const disabledContextShape: IDisabledContext = useContext<IDisabledContext>(DisabledContext);
  const [generatedFields, setGeneratedFields] = useState<ReactElement[]>();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const isJa: boolean = useSelector(isJASelector);
  const isFaqOpen: boolean = useSelector(isFaqOpenSelector);
  const activityTypes = useSelector(activityTypesSelector);
  const amaCreditTerm: ITaxonomyTerm = useSelector(amaCreditTermIdSelector);
  const allBoards: IBoardSlim[] = useSelector(boardSelector);
  const configurationDictionary: IDictionary<IBCTBoard> = useSelector(configuredBoardsDictionarySelector);
  const configuredBoards: IBCTBoard[] = useSelector(configuredBoardsSelector);
  const ipceCreditId = useSelector(ipceCreditTermIdSelector)?.id;
  const pharmacyCreditsTermId = useSelector(pharmacyCreditTermIdSelector)?.id;
  const nextSequenceNumber: number = useSelector(activitySelector)?.nextSequenceNumber;
  const amaCreditId = amaCreditTerm?.id;
  const activityTitle = activity?.title;
  const activityTypeId = activity?.typeId;
  const initialMocBoardIds: string[] = keys(activity?.boardMocDetails);
  const initialRemsBoardIds: string[] = keys(activity?.boardRemsDetails);

  // 4386 - depending on activity type and the currently selected boards, some boards need to be filtered
  const allowedBoardSelections: IBoardSlim[] = getAllowedBoardSelections(boards, activityTypeId, [
    ...initialMocBoardIds,
    ...initialRemsBoardIds,
  ]);
  const allMocBoards: IBoardSlim[] = allowedBoardSelections?.filter(
    ({ type }: IBoardSlim): boolean => type === BoardTypes.CERTIFYING_BOARD,
  );
  const allRemsBoards: IBoardSlim[] = allowedBoardSelections?.filter(
    ({ type }: IBoardSlim): boolean => type === BoardTypes.REMS,
  );

  // Separate boards by board type
  const configuredMocBoards: IBCTBoard[] = useFilterHook(BoardTypes.CERTIFYING_BOARD, configuredBoards);
  const configuredRemsBoards: IBCTBoard[] = useFilterHook(BoardTypes.REMS, configuredBoards);

  const isPharmacyCreditsChecked: boolean = values?.credits?.[pharmacyCreditsTermId] > 0;
  const isPharmacyAbbreviationL: boolean =
    activityTypes?.find((type: ActivityType): boolean => type.id === values?.typeId)?.pharmacyAbbreviation === 'L';

  const activityType = activityTypes?.find(({ id }: ActivityType) => id === values?.typeId);
  const doesAMACreditHaveValue = !!values?.credits?.[amaCreditId];
  const doesIPCECreditHaveValue = !!values?.credits?.[ipceCreditId];

  const pharmacyQuestionFields = [
    'targetAudience',
    'pharmacyTypeId',
    'isPharmacyCertificateProgram',
    'pharmacyTopicId',
    'pharmacyLiveDates',
  ];

  const cmePassportQuestionFields = ['detailsUrl', 'isRestrictedAudience', 'participationFeeTypeId'];

  // This whole `setActiveFieldClass` is not optimal and needs to be refactored. This is happening on every
  // formik state change.
  const setActiveFieldClass = () => {
    // The following cannot be done via the template because it comes from taxonomy.
    // Hide/Show these group when the page renders.
    if (doesAMACreditHaveValue || (isJa && doesIPCECreditHaveValue)) {
      document.querySelector('[data-group-id="includeInCmeFinderGroup"]')?.classList.remove('hidden');
      document.querySelector('[data-group-id="isRestrictedAudienceGroup"]')?.classList.remove('hidden');
      document.querySelector('[data-group-id="detailsUrlGroup"]')?.classList.remove('hidden');
    } else {
      document.querySelector('[data-group-id="includeInCmeFinderGroup"]')?.classList.add('hidden');
      document.querySelector('[data-group-id="isRestrictedAudienceGroup"]')?.classList.add('hidden');
      document.querySelector('[data-group-id="detailsUrlGroup"]')?.classList.add('hidden');
    }

    if (isPharmacyCreditsChecked) {
      document.querySelector('[data-group-id="pharmacyQuestions"]')?.classList.remove('hidden');
      if (isPharmacyAbbreviationL) {
        document.querySelector('[data-question="pharmacyLiveDates"]')?.parentElement?.classList.remove('hidden');
      } else {
        document.querySelector('[data-question="pharmacyLiveDates"]')?.parentElement?.classList.add('hidden');
      }
    } else {
      document.querySelector('[data-group-id="pharmacyQuestions"]')?.classList.add('hidden');
    }
  };

  setActiveFieldClass();

  // Show the pharmacy questions when the pharmacy checkbox is checked.
  // Cannot be done via the template because it comes from taxonomy.
  useEffect(() => {
    if (isPharmacyCreditsChecked) {
      document.querySelector('[data-group-id="pharmacyQuestions"]')?.classList.remove('hidden');
      // If is-recurring was selected, don't show a number input, show a message instead.
      if (values.recurring === 'recurring-true') {
        const node = document.querySelector('[data-question="pharmacySequenceNumber"]');
        if (node) {
          const message = document.createElement('div');
          message.setAttribute('class', 'caption-text form-input');
          message.innerHTML =
            'For recurring activities, the sequence number for the UAN will be autogenerated for each activity instance.';
          node.parentNode.replaceChild(message, node);
        }
      }
    } else {
      document.querySelector('[data-group-id="pharmacyQuestions"]')?.classList.add('hidden');
    }
  }, [isPharmacyCreditsChecked, values?.credits, values?.recurring]);

  // Since the Pharmacy Questions' visibility is custom, we have to rely on this hook to reset fields on hide
  useEffect(() => {
    if (!isPharmacyCreditsChecked && initialValues) {
      pharmacyQuestionFields.forEach((fieldKey) => setFieldValue(fieldKey, initialValues?.[fieldKey]));
    }
  }, [isPharmacyCreditsChecked]);

  // 4793: Since the CME Passport Questions' visibility is custom, we have to rely on this hook to reset fields on hide
  useEffect(() => {
    if ((!doesAMACreditHaveValue || (isJa && !doesIPCECreditHaveValue)) && initialValues) {
      cmePassportQuestionFields.forEach((fieldKey) => setFieldValue(fieldKey, initialValues?.[fieldKey]));
      setFieldValue('includeInCmeFinder', 'includeInCmeFinder-false');
    }
  }, [doesAMACreditHaveValue, isJa, doesIPCECreditHaveValue]);

  useEffect(() => {
    const hasActivity = !!activity;
    const hasTerms = !!terms;
    const hasBoards = boards && boards?.length;
    const hasModernBoards = allBoards && allBoards?.length;
    const hasTypes = types && types?.length;
    const hasValues = !!values;
    const selectedTemplate = activity?.formTemplate?.[editIndex];
    const hasTemplateIndex = !!selectedTemplate;
    const shouldRegenerate =
      hasActivity && hasTerms && hasBoards && hasModernBoards && hasTypes && hasValues && hasTemplateIndex;
    if (shouldRegenerate) {
      setGeneratedFields(
        FormService.buildForm({
          activities: activity ? [activity] : null,
          allMocBoards,
          allRemsBoards,
          amaCreditValue: values?.credits?.[amaCreditTerm?.id] as number,
          configurationDictionary,
          configuredMocBoards,
          configuredRemsBoards,
          disabledContext: disabledContextShape,
          formMeta: [selectedTemplate],
          formikBag: {
            errors,
            handleChange,
            initialValues: defaultAddFormProps,
            setFieldValue,
            touched,
            values,
          },
          isJa,
          selectedRemsBoardIds: values?.supportedRemsIds,
          selectedSpecialtyBoardIds: values?.boardMocDetailsSelection,
          terms,
        }),
      );
    }
  }, [
    activity,
    allBoards,
    boards,
    errors,
    terms,
    touched,
    types,
    values?.commercialSupportSourceKind,
    values?.hasCommercialSupport,
    values?.isJointlyProvided,
    values?.isMoc,
    values?.isRems,
    values?.markedForCommendationCriteria,
    values?.recurring,
    values?.typeId,
    nextSequenceNumber,
  ]);

  useEffect(() => {
    const activityMocIds = keys(activity?.boardMocDetails);
    const activityRemsIds = keys(activity?.boardRemsDetails);
    const missingConfigIds = [...activityMocIds, ...activityRemsIds]?.filter((id) => !configurationDictionary?.[id]);
    const metadataPromises: ThunkAction<Promise<void>, unknown, null, PARSAction<unknown>>[] = missingConfigIds?.length
      ? missingConfigIds?.map((id) => dispatch(getBoardById(id)))
      : [];

    Promise.all(metadataPromises);
  }, [activity?.boardMocDetails, activity?.boardRemsDetails]);

  // Default to next available sequence number if one was provided via API
  // Only add to the model if the pharmacy credits are checked
  // Remove from the model when pharmacy credits is unchecked
  useEffect(() => {
    const isSequenceNumberValid = isNumber(values?.pharmacySequenceNumber);
    const isPharmacyCreditsChecked = !!values?.credits?.[`credits${pharmacyCreditsTermId}`];
    if (!isSequenceNumberValid && !!isPharmacyCreditsChecked) {
      setFieldValue('pharmacySequenceNumber', nextSequenceNumber);
    }
    if (isSequenceNumberValid && !isPharmacyCreditsChecked) {
      setFieldValue('pharmacySequenceNumber', undefined);
    }
  }, [nextSequenceNumber, values?.credits]);

  useEffect(() => {
    const isBoardConfigAvailable = !!allBoards?.length;
    if (!isBoardConfigAvailable) {
      dispatch(getAllBoards());
    }
  }, [allBoards, dispatch]);

  const shouldShowLocationInformation =
    !!values?.locationType?.includes('InPerson') &&
    (activityType?.abbreviation?.includes(ACTIVITY_TYPE_ABBREVIATION_ENUM.C) ||
      activityType?.abbreviation?.includes(ACTIVITY_TYPE_ABBREVIATION_ENUM.RSS));
  const shouldShowRepeater = false; // Never show the repeater controls on the Edit screen

  const faqs: ITaxonomyTerm[] = (props.terms[TAXONOMY_FAQ_ROOT_ID] || {})?.terms || [];

  let faqArea = '';
  let faqTitle = '';

  switch (editIndex) {
    case 0:
      faqArea = 'Basics';
      faqTitle = 'Basics FAQs';
      break;

    case 1:
      faqArea = 'Learners';
      faqTitle = 'Information for Learners FAQs';
      break;

    case 2:
      faqArea = 'Accreditation';
      faqTitle = 'Accreditation Details FAQs';
      break;

    case 3:
      faqArea = 'MOC';
      faqTitle = 'MOC FAQs';
      break;

    case 4:
      faqArea = 'REMS';
      faqTitle = 'REMS FAQs';
      break;
  }

  const onClickCancelWithoutSaving = () => {
    setIsConfirmDialogOpen(true);
  };

  const closeFaqSidebar = () => {
    dispatch(closeFaq());
  };

  const onCloseAndNavigateAway = () => {
    setIsConfirmDialogOpen(false);
    history.push(`/activities/detail/${idParam}`);
  };

  const onCloseAndSave = async () => {
    setIsConfirmDialogOpen(false);
    await submitForm();
  };

  const handleSave = async () => {
    await submitForm();
  };

  const typeIdDisabledState: IDisabledState = get(disabledContextShape, 'typeId') as IDisabledState;
  return (
    <>
      <section className={`form-container ${styles['edit-activity-container']}`}>
        <div className={classNames('form-title-container', styles['form-title-container'])}>
          <div className="eyebrow">Update an activity</div>
          <HighlightBox variant="warning">
            <Box display="flex" component="p">
              <WarningRounded />
              <Box ml={2}>Updates can only be made to editable fields.</Box>
            </Box>
          </HighlightBox>
          <h4>{activityTitle}</h4>
        </div>
        <form onSubmit={handleSubmit} className="static-form">
          {editIndex === 0 && (
            <>
              <div data-template-id="Basics" className="form-group">
                <InputBlock
                  required
                  suppressLabel
                  errors={errors}
                  touched={touched}
                  name="title"
                  title="What's the name of the activity"
                >
                  <Field
                    aria-label="Activity Name"
                    id="title"
                    name="title"
                    placeholder="Enter Activity Name"
                    type="text"
                    aria-invalid={!!errors?.title}
                  />
                </InputBlock>
              </div>
              <div data-template-id="Basics" className="form-group">
                <InputBlock
                  ariaLabel="Activity Format"
                  required
                  suppressLabel
                  errors={errors}
                  touched={touched}
                  name="typeId"
                  role="radiogroup"
                  title="What is the activity format?"
                >
                  {types.map(({ id, title, description }: ActivityType) => (
                    <label htmlFor={id} key={id} className="form-input-radio">
                      <Field
                        aria-invalid={!!errors?.typeId?.length}
                        disabled={typeIdDisabledState?.isDisabled && typeIdDisabledState?.disabledOptions?.includes(id)}
                        id={id}
                        name="typeId"
                        type="radio"
                        value={id}
                      />
                      <span className="checkmark">
                        <div className="label-text-container">
                          <div className="label-text">{title}</div>
                          <div className="caption-text">{description}</div>
                        </div>
                      </span>
                    </label>
                  ))}
                </InputBlock>
              </div>
            </>
          )}
          {generatedFields}
          {editIndex === 0 && (
            <div data-template-id="Basics" className="form-group">
              <LocationDetailsRepeater
                isDisabled={false}
                formikKey="tempDatesAndLocation"
                shouldShowLocationInformation={shouldShowLocationInformation}
                shouldShowRepeater={shouldShowRepeater}
              />
            </div>
          )}
          <div className={styles['button-row']}>
            <Button variant={ButtonVariant.Tertiary} onClick={onClickCancelWithoutSaving}>
              <KeyboardBackspaceRounded className="tertiary-icon-back" />
              Cancel Without Saving
            </Button>
            <Button
              disabled={!isValid || isSubmitting}
              onClick={handleSave}
              startIcon={isSubmitting && <CircularProgress color="inherit" size="1rem" />}
              variant={ButtonVariant.Primary}
            >
              Save
            </Button>
          </div>
        </form>
        <Modal
          isOpen={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          title="Do you want to save your changes before quitting?"
        >
          <div className={styles['button-row']}>
            <Button variant={ButtonVariant.Secondary} onClick={onCloseAndNavigateAway}>
              No, Quit Without Saving
            </Button>
            <Button disabled={!isValid || isSubmitting} variant={ButtonVariant.Primary} onClick={onCloseAndSave}>
              Yes, Save and Quit
            </Button>
          </div>
        </Modal>
      </section>
      <aside className={classNames('rail-container rail-container--75', { open: isFaqOpen })}>
        <Faq faqTitle={faqTitle} faqs={faqs} faqArea={faqArea} isJa={isJa} handleClose={closeFaqSidebar} />
      </aside>
    </>
  );
};

const EditActivityForm = withFormik<IEditActivityProps, ActivityFormStateValues>({
  enableReinitialize: true,
  handleSubmit: async (values, { props, setSubmitting, setStatus }) => {
    const { handleSubmit } = props;
    setSubmitting(true);
    try {
      await handleSubmit(values);
    } catch (e) {
      setStatus(e);
    }
    setSubmitting(false);
  },
  mapPropsToValues: (props: IEditActivityProps): ActivityFormStateValues => {
    const { activity, dictionaries } = props;
    const supportSources = ['Monetary', 'NonMonetary'];
    if (activity && dictionaries) {
      const { creditTypesDictionary } = dictionaries;
      const {
        commercialSupportSources = [],
        jointProviders = [],
        boardMocDetails,
        boardRemsDetails,
        city,
        country,
        credits,
        endDate,
        postalCode,
        startDate,
        stateOrProvince,
        targetAudience,
        ...restActivity
      } = activity;

      const recreateMonetarySupportSourceOption = ({
        source,
        amountGiven,
      }: CommercialSupportSource): IMonetarySupportSourceOption => ({
        amountGiven,
        id: source,
        source: source,
      });
      const recreateInKindSupportSourceOption = ({ source }: CommercialSupportSource): IInKindSupportSourceOption =>
        source;
      // Split commercialSupportSources into In-Kind and Monetary
      const inKindSupportSources: CommercialSupportSource[] = commercialSupportSources.filter(
        ({ hasInKindSupport }): boolean => hasInKindSupport,
      );
      const monetarySupportSources: CommercialSupportSource[] = commercialSupportSources.filter(
        ({ hasInKindSupport, amountGiven }): boolean => hasInKindSupport === false || amountGiven !== undefined,
      );

      const commercialMonetarySupportSources: IMonetarySupportSourceOption[] = monetarySupportSources.map(
        recreateMonetarySupportSourceOption,
      );

      const commercialInKindSupportSources: IInKindSupportSourceOption[] = inKindSupportSources.map(
        recreateInKindSupportSourceOption,
      );

      const commercialSupportSourceKind: string[] = supportSources.filter((sourceKind): boolean => {
        if (sourceKind === 'Monetary') {
          return !!monetarySupportSources?.length;
        }
        if (sourceKind === 'NonMonetary') {
          return !!inKindSupportSources?.length;
        }
        return false;
      });

      const boardIds = keys(boardMocDetails);
      const remsIds = keys(boardRemsDetails);

      // Deep-clone to avoid reference equality in deep JS objects
      const deepCloneBoardMocDetails: IDictionary<BoardMocDetails> = cloneDeep(boardMocDetails);
      const deepCloneBoardRemsDetails: IDictionary<IBoardRemsDetails> = cloneDeep(boardRemsDetails);

      const targetAudienceList = targetAudience?.map((audience) => lowerFirst(audience)) || [];

      // Construct the selected checkboxes and initial values for the credits fields
      const allCreditTypeKeys: string[] = keys(creditTypesDictionary);
      const creditKeys: string[] = keys(credits);
      const creditsCheckboxes: IDictionary<boolean> = {};
      const creditsInitialValues: IDictionary<number> = {};

      creditKeys?.forEach((key: string): void => {
        creditTypesDictionary[key]
          ? (creditsCheckboxes[`credits${key}`] = true)
          : (creditsCheckboxes[`credits${key}`] = false);
      });

      allCreditTypeKeys?.forEach((key: string): void => {
        typeof credits[key] === 'number' ? (creditsInitialValues[key] = credits[key]) : (creditsInitialValues[key] = 0);
      });

      const dateAndLocationEntry = {
        city,
        country,
        endDate,
        postalCode,
        startDate,
        stateOrProvince,
      };
      const tempDatesAndLocation = [dateAndLocationEntry];

      // Possible values of activity.locationType
      // - 'In-Person'
      // - 'Live-Streamed'
      // - 'Online'
      // - 'Other'
      const transformedLocationType = activity?.locationType?.map((location) =>
        location?.replace(' ', '')?.replace('-', '')?.replace('S', 's'),
      );

      return {
        ...restActivity,
        boardMocDetails: deepCloneBoardMocDetails,
        boardMocDetailsSelection: boardIds,
        boardRemsDetails: deepCloneBoardRemsDetails,
        commercialInKindSupportSources,
        commercialMonetarySupportSources,
        commercialSupportSourceKind,
        credits: {
          ...creditsInitialValues,
          ...creditsCheckboxes,
        },
        hasCommercialSupport: activity?.hasCommercialSupport
          ? 'hasCommercialSupport-true'
          : 'hasCommercialSupport-false',
        includeInCmeFinder: activity?.includeInCmeFinder ? 'includeInCmeFinder-true' : 'includeInCmeFinder-false',
        isIpce: activity?.isIpce ? 'isIpce-true' : 'isIpce-false',
        isJointlyProvided: activity?.isJointlyProvided ? 'joint' : 'direct',
        isMips: activity?.isMips || activity?.isMips ? 'isMips-true' : 'isMips-false',
        isMoc: activity?.isMoc || activity?.isMoc ? 'isMoc-true' : 'isMoc-false',
        isPharmacyCertificateProgram: activity?.isPharmacyCertificateProgram
          ? 'isPharmacyCertificateProgram-true'
          : 'isPharmacyCertificateProgram-false',
        isRems: activity?.isRems || activity?.isRems ? 'isRems-true' : 'isRems-false',
        isRestrictedAudience:
          activity?.isRestrictedAudience === true ? 'limited' : activity?.isRestrictedAudience === false ? 'open' : '',
        jointProviders,
        locationType: transformedLocationType,
        markedForCommendationCriteria: activity?.markedForCommendationCriteria
          ? 'markedForCommendationCriteria-true'
          : 'markedForCommendationCriteria-false',
        mocProgramAttestation: activity?.mocProgramAttestation ? ['mocProgramAttestation'] : [],
        supportedRemsIds: remsIds,
        targetAudience: targetAudienceList,
        tempDatesAndLocation,
      };
    }
  },
  validateOnBlur: true,
  validateOnChange: false,
  validationSchema: ({ schemas, editIndex }: IEditActivityProps) =>
    !!schemas && isNumber(editIndex) && schemas[editIndex],
})(EditActivityFormik);

export default EditActivityForm;
